import { Box, TextField, Typography, Grid } from "@mui/material";
import {
  DatePicker,
  DateType,
  EmailIcon,
  Field,
  NumberField,
  PhoneIcon,
} from "ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { parse } from "date-fns";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import { Layout } from "../shared/Layout";

type AddressData = {
  name: string;
  street: string;
  number_and_addition: string;
  postal_code: string;
  city: string;
  phone_number: string;
  email_address: string;
};

type MultiLanguageText = {
  language: string;
  text: string;
};

type AddressFieldConfig = {
  required: boolean;
  hidden: boolean;
  label: string | null;
  placeholder: string | null;
};

type AddressConfig = {
  name?: AddressFieldConfig;
  street?: AddressFieldConfig;
  number_and_addition?: AddressFieldConfig;
  postal_code?: AddressFieldConfig;
  city?: AddressFieldConfig;
  phone_number?: AddressFieldConfig;
  email_address?: AddressFieldConfig;
};

type OpenAnswerProps = {
  simulated?: boolean;
  title: string;
  loading: boolean;
  disabled?: boolean;
  buttonLabel?: string;
  settings: {
    answer_format?:
      | "text"
      | "number"
      | "currency"
      | "date"
      | "email"
      | "phone_number"
      | "address";
    enable_answer_format?: boolean;
    enable_require_response?: boolean;
    min?: number | string;
    max?: number | string;
    address_config?: AddressConfig;
  };
  onContinue: (answer: string) => void;
  inputPlaceholderLabel?: string;
};

export const OpenAnswer = ({
  title,
  settings,
  onContinue,
  buttonLabel = "Continue",
  loading,
  disabled,
  inputPlaceholderLabel,
}: OpenAnswerProps) => {
  const [answer, setAnswer] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [addressData, setAddressData] = useState<AddressData>({
    name: "",
    street: "",
    number_and_addition: "",
    postal_code: "",
    city: "",
    phone_number: "",
    email_address: "",
  });
  const [addressErrors, setAddressErrors] = useState<Partial<Record<keyof AddressData, string>>>({});

  const {
    answer_format: type,
    enable_answer_format,
    enable_require_response,
    max,
    min,
    address_config,
  } = settings || {};

  const questionType = useMemo(() => {
    if (enable_answer_format) {
      return type;
    }
    return "text";
  }, [enable_answer_format, type]);

  // Address validation functions
  const validateAddressField = (field: keyof AddressData, value: string) => {
    const fieldConfig = address_config?.[field];
    const errors = { ...addressErrors };

    if (fieldConfig?.required && !value.trim()) {
      errors[field] = "This field is required";
    } else if (field === "email_address" && value.trim()) {
      const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
      if (!emailRegex.test(value)) {
        errors[field] = "Please enter a valid email address";
      } else {
        delete errors[field];
      }
    } else if (field === "phone_number" && value.trim()) {
      try {
        // Add + prefix if not present and value doesn't start with +
        const phoneValue = value.trim().startsWith('+') ? value.trim() : `+${value.trim()}`;
        const phoneNumber = parsePhoneNumberFromString(phoneValue);
        if (!phoneNumber || !phoneNumber.isValid()) {
          errors[field] = "Please enter a valid phone number";
        } else {
          delete errors[field];
        }
      } catch (error) {
        errors[field] = "Please enter a valid phone number";
      }
    } else {
      delete errors[field];
    }

    setAddressErrors(errors);
  };

  const isAddressValid = useMemo(() => {
    if (questionType !== "address") return true;

    // Check if any required fields are empty
    const requiredFields = Object.entries(address_config || {}).filter(
      ([_, config]) => config?.required && !config?.hidden
    );

    for (const [field] of requiredFields) {
      const value = addressData[field as keyof AddressData];
      if (!value?.trim()) {
        return false;
      }
    }

    // Check if there are any validation errors
    return Object.keys(addressErrors).length === 0;
  }, [questionType, address_config, addressData, addressErrors]);

  const isDisabled = useMemo(() => {
    // Handle address validation
    if (questionType === "address") {
      return !isAddressValid;
    }

    const value =
      questionType === "text" ? answer?.trim()?.length : Number(answer);
    const isCheckable = enable_answer_format && questionType !== "date";

    if (
      questionType &&
      enable_answer_format &&
      (enable_require_response || answer) &&
      ["email", "phone_number"].includes(questionType)
    ) {
      if (questionType === "phone_number") {
        const cleanedNumber = answer.replace(/[\s()-]/g, "");
        setAnswer(cleanedNumber);
      }

      if (questionType === "email") {
        const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
        if (!emailRegex.test(answer)) {
          return true;
        }
             } else if (questionType === "phone_number") {
         try {
           // Add + prefix if not present and value doesn't start with +
           const phoneValue = answer.trim().startsWith('+') ? answer.trim() : `+${answer.trim()}`;
           const phoneNumber = parsePhoneNumberFromString(phoneValue);
           if (!phoneNumber || !phoneNumber.isValid()) {
             return true;
           }
         } catch (error) {
           return true;
         }
       }
    }

    if (answer?.trim()?.length === 0 && !enable_require_response) {
      return false;
    }

    if (
      isCheckable &&
      ((max && value > Number(max)) || (min && value < Number(min)))
    ) {
      return true;
    }

    if (enable_answer_format && (min || max) && questionType === "date") {
      const answerDate = parseDate(answer);
      const validMin = parseDate(min as string);
      const validMax = parseDate(max as string);

      if (
        !answer ||
        (validMin && answerDate < validMin) ||
        (validMax && answerDate > validMax)
      ) {
        return true;
      }
    }
    if (enable_require_response) {
      return !answer?.trim();
    }

    return false;
  }, [
    enable_require_response,
    questionType,
    answer,
    min,
    max,
    enable_answer_format,
    isAddressValid,
  ]);

  useEffect(() => {
    return () => {
      // cleanup
      setAnswer("");
      setAddressData({
        name: "",
        street: "",
        number_and_addition: "",
        postal_code: "",
        city: "",
        phone_number: "",
        email_address: "",
      });
      setAddressErrors({});
    };
  }, [settings]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [maxRows, setMaxRows] = useState<number>(16);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const resizeHandler = () => {
      if (containerRef.current) {
        const { height, width } = containerRef.current.getBoundingClientRect();
        const maxRowsValue = Math.round(height / 26);

        setMaxRows(width < 600 ? maxRowsValue : 16);
      }
    };

    resizeHandler();

    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  const validateEmail = (value: string) => {
    const regex: {
      [key: string]: RegExp;
    } = {
      email: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
    };
    if (regex.email && !regex.email.test(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  const handleContinue = () => {
    if (questionType === "address") {
      // Clean up address data by removing empty fields and only including visible fields
      const cleanedAddressData: Partial<AddressData> = {};
      
      // Only include fields that are not hidden and have values (or are required)
      Object.entries(addressData).forEach(([key, value]) => {
        const fieldName = key as keyof AddressData;
        const fieldConfig = address_config?.[fieldName];
        
        // Include field if it's not hidden and either has a value or is required
        if (!fieldConfig?.hidden && (value.trim() || fieldConfig?.required)) {
          cleanedAddressData[fieldName] = value.trim();
        }
      });
      
      // Convert to JSON string
      const addressJson = JSON.stringify(cleanedAddressData);
      onContinue(addressJson);
    } else {
      onContinue(answer);
    }
  };

  return (
    <Layout
      title={title}
      onContinue={handleContinue}
      disabled={isDisabled || disabled}
      loading={loading}
      buttonLabel={buttonLabel}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          pt: {
            xs: "24px",
            sm: "40px",
          },
          pb: {
            xs: "24px",
            sm: "24px",
          },
          px: {
            xs: "24px",
            sm: "0px",
          },
        }}
      >
        {questionType === "text" && (
          <TextField
            multiline
            sx={{
              width: "100%",
            }}
            rows={maxRows}
            placeholder={inputPlaceholderLabel || "Please provide an answer"}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        )}
        {questionType === "number" && (
          <NumberField
            clearable
            avoidDecimals
            defaultValue={answer.trim() === "" ? null : Number(answer)}
            onChange={(value) =>
              setAnswer(value === null ? "" : value.toString())
            }
            placeholder={inputPlaceholderLabel || "Please provide a number"}
          />
        )}
        {questionType === "currency" && (
          <NumberField
            clearable
            defaultValue={answer.trim() === "" ? null : Number(answer)}
            onChange={(value) =>
              setAnswer(value === null ? "" : value.toString())
            }
            placeholder={inputPlaceholderLabel || "Please provide a price"}
            icon={<Box>$</Box>}
          />
        )}
        {questionType === "date" && (
          <DatePicker
            defaultValue={getFormattedDate(answer)}
            calendarProps={{
              minimumDate: getFormattedDate(min as string),
              maximumDate: getFormattedDate(max as string),
            }}
            onChange={(value) => setAnswer(formatDate(value))}
          />
        )}
        {questionType === "email" && (
          <>
            <Field
              type="email"
              sx={{
                width: "100%",
                position: "relative",
                paddingLeft: "50px",
                overflow: "hidden",
              }}
              placeholder="example@email.com"
              value={answer}
              component={{
                position: "left",
                render: <EmailIcon color="var(--mui-palette-input_fields-text-color)" />,
                styles: {
                  width: "38px",
                  height: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  left: 2,
                  borderRadius: "8px 0px 0px 8px",
                  borderRight: "1px solid var(--mui-palette-layout-border-color)",
                },
              }}
              onChange={(val) => setAnswer(val)}
              onBlur={(value) => validateEmail(value)}
            />
            {emailError && <Typography color="error">{emailError}</Typography>}
          </>
        )}
        {questionType === "phone_number" && (
          <Field
            type="tel"
            sx={{
              width: "100%",
              position: "relative",
              paddingLeft: "50px",
              overflow: "hidden",
            }}
            placeholder="+123 456 7890"
            value={answer}
            component={{
              position: "left",
              render: <PhoneIcon color="var(--mui-palette-input_fields-text-color)" />,
              styles: {
                width: "38px",
                height: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                left: 2,
                borderRadius: "8px 0px 0px 8px",
                borderRight: "1px solid var(--mui-palette-layout-border-color)",
              },
            }}
            onChange={(val) => {
              // Ensure + prefix is added if not present and user enters digits
              let formattedVal = val;
              if (val && !val.startsWith('+') && /^\d/.test(val.trim())) {
                formattedVal = `+${val}`;
              }
              setAnswer(formattedVal);
            }}
          />
        )}
        {questionType === "address" && (
          <Box sx={{ width: "100%" }}>
            <Grid container spacing={3}>
              {/* Name Field */}
              {!address_config?.name?.hidden && (
                <Grid item xs={12}>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 1, 
                        fontWeight: 500,
                        color: "text.primary"
                      }}
                    >
                      {address_config?.name?.label || "Full Name"} {address_config?.name?.required && (
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: 'error.main', 
                            fontWeight: 'bold',
                            ml: 0.5
                          }}
                        >
                          *
                        </Typography>
                      )}
                    </Typography>
                    <Field
                      type="text"
                      sx={{ 
                        width: "100%",
                        ...(address_config?.name?.required && addressErrors.name && {
                          '& .MuiOutlinedInput-root': {
                            borderColor: 'error.main',
                            '&:hover': {
                              borderColor: 'error.main',
                            }
                          }
                        })
                      }}
                      placeholder={address_config?.name?.placeholder || "Enter your full name"}  
                      value={addressData.name}
                      onChange={(val) => {
                        setAddressData(prev => ({ ...prev, name: val }));
                        validateAddressField("name", val);
                      }}
                      onBlur={(val) => validateAddressField("name", val)}
                    />
                    {addressErrors.name && (
                      <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                        {addressErrors.name}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Street and Number Row - Only render if at least one field is visible */}
              {(!address_config?.street?.hidden || !address_config?.number_and_addition?.hidden) && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Street Field */}
                    {!address_config?.street?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.number_and_addition?.hidden ? 8 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.street?.label || "Street"} {address_config?.street?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="text"
                            sx={{ 
                              width: "100%",
                              ...(address_config?.street?.required && addressErrors.street && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.street?.placeholder || "Street name"}
                            value={addressData.street}
                            onChange={(val) => {
                              setAddressData(prev => ({ ...prev, street: val }));
                              validateAddressField("street", val);
                            }}
                            onBlur={(val) => validateAddressField("street", val)}
                          />
                          {addressErrors.street && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.street}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* Number and Addition Field */}
                    {!address_config?.number_and_addition?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.street?.hidden ? 4 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.number_and_addition?.label || "Number & Addition"} {address_config?.number_and_addition?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="text"
                            sx={{ 
                              width: "100%",
                              ...(address_config?.number_and_addition?.required && addressErrors.number_and_addition && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.number_and_addition?.placeholder || "No. & Addition"}
                            value={addressData.number_and_addition}
                            onChange={(val) => {
                              setAddressData(prev => ({ ...prev, number_and_addition: val }));
                              validateAddressField("number_and_addition", val);
                            }}
                            onBlur={(val) => validateAddressField("number_and_addition", val)}
                          />
                          {addressErrors.number_and_addition && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.number_and_addition}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* Postal Code and City Row - Only render if at least one field is visible */}
              {(!address_config?.postal_code?.hidden || !address_config?.city?.hidden) && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Postal Code Field */}
                    {!address_config?.postal_code?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.city?.hidden ? 4 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.postal_code?.label || "Postal Code"} {address_config?.postal_code?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="text"
                            sx={{ 
                              width: "100%",
                              ...(address_config?.postal_code?.required && addressErrors.postal_code && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.postal_code?.placeholder || "12345"}
                            value={addressData.postal_code}
                            onChange={(val) => {
                              setAddressData(prev => ({ ...prev, postal_code: val }));
                              validateAddressField("postal_code", val);
                            }}
                            onBlur={(val) => validateAddressField("postal_code", val)}
                          />
                          {addressErrors.postal_code && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.postal_code}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* City Field */}
                    {!address_config?.city?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.postal_code?.hidden ? 8 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.city?.label || "City"} {address_config?.city?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="text"
                            sx={{ 
                              width: "100%",
                              ...(address_config?.city?.required && addressErrors.city && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.city?.placeholder || "City name"}
                            value={addressData.city}
                            onChange={(val) => {
                              setAddressData(prev => ({ ...prev, city: val }));
                              validateAddressField("city", val);
                            }}
                            onBlur={(val) => validateAddressField("city", val)}
                          />
                          {addressErrors.city && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.city}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {/* Contact Information Row - Only render if at least one field is visible */}
              {(!address_config?.phone_number?.hidden || !address_config?.email_address?.hidden) && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Phone Number Field */}
                    {!address_config?.phone_number?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.email_address?.hidden ? 6 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.phone_number?.label || "Phone Number"} {address_config?.phone_number?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="tel"
                            sx={{
                              width: "100%",
                              position: "relative",
                              paddingLeft: "50px",
                              overflow: "hidden",
                              ...(address_config?.phone_number?.required && addressErrors.phone_number && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.phone_number?.placeholder || "+1 (555) 123-4567"}
                            value={addressData.phone_number}
                            component={{
                              position: "left",
                              render: <PhoneIcon color="var(--mui-palette-input_fields-text-color)" />,
                              styles: {
                                width: "38px",
                                height: "38px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                left: 2,
                                borderRadius: "8px 0px 0px 8px",
                                borderRight: "1px solid var(--mui-palette-layout-border-color)",
                              },
                            }}
                            onChange={(val) => {
                              // Ensure + prefix is added if not present and user enters digits
                              let formattedVal = val;
                              if (val && !val.startsWith('+') && /^\d/.test(val.trim())) {
                                formattedVal = `+${val}`;
                              }
                              setAddressData(prev => ({ ...prev, phone_number: formattedVal }));
                              validateAddressField("phone_number", formattedVal);
                            }}
                            onBlur={(val) => validateAddressField("phone_number", val)}
                          />
                          {addressErrors.phone_number && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.phone_number}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* Email Address Field */}
                    {!address_config?.email_address?.hidden && (
                      <Grid 
                        item 
                        xs={12} 
                        sm={!address_config?.phone_number?.hidden ? 6 : 12}
                      >
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 500,
                              color: "text.primary"
                            }}
                          >
                            {address_config?.email_address?.label || "Email Address"} {address_config?.email_address?.required && (
                              <Typography 
                                component="span" 
                                sx={{ 
                                  color: 'error.main', 
                                  fontWeight: 'bold',
                                  ml: 0.5
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </Typography>
                          <Field
                            type="email"
                            sx={{
                              width: "100%",
                              position: "relative",
                              paddingLeft: "50px",
                              overflow: "hidden",
                              ...(address_config?.email_address?.required && addressErrors.email_address && {
                                '& .MuiOutlinedInput-root': {
                                  borderColor: 'error.main',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                  }
                                }
                              })
                            }}
                            placeholder={address_config?.email_address?.placeholder || "john.doe@example.com"}
                            value={addressData.email_address}
                            component={{
                              position: "left",
                              render: <EmailIcon color="var(--mui-palette-input_fields-text-color)" />,
                              styles: {
                                width: "38px",
                                height: "38px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                left: 2,
                                borderRadius: "8px 0px 0px 8px",
                                borderRight: "1px solid var(--mui-palette-layout-border-color)",
                              },
                            }}
                            onChange={(val) => {
                              setAddressData(prev => ({ ...prev, email_address: val }));
                              validateAddressField("email_address", val);
                            }}
                            onBlur={(val) => validateAddressField("email_address", val)}
                          />
                          {addressErrors.email_address && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                              {addressErrors.email_address}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

const parseDate = (date: string) => {
  const dateObj = parse(date, "dd-MM-yyyy", new Date());
  return dateObj;
};

const getFormattedDate = (date: string) => {
  if (!date) return undefined;

  const [day, month, year] = date.split("-");

  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  };
};

const formatDate = (date: DateType) => {
  if (!date) return "";

  const { day, month, year } = date;

  return `${day}-${month}-${year}`;
};
